import {
    Observer,
    Rotation_EQJ_HOR,
    SiderealTime
} from 'astronomy-engine'

import { OBSERVATORY } from '@/constants/observatory'

export const DEG = Math.PI / 180
export const RAD = 180 / Math.PI

export const observer = new Observer(OBSERVATORY.latitude, OBSERVATORY.longitude, OBSERVATORY.elevation)

/** Row-major 3x3 matrix taking J2000 equatorial unit vectors to the horizon frame (x north, y west, z up). */
export type Mat3 = Float64Array

export const eqjToHorizon = (date: Date): Mat3 => {
    const { rot } = Rotation_EQJ_HOR(date, observer)
    const m = new Float64Array(9)

    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            m[i * 3 + j] = rot[j][i]
        }
    }

    return m
}

export const transpose3 = (m: Mat3): Mat3 => {
    const t = new Float64Array(9)

    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            t[i * 3 + j] = m[j * 3 + i]
        }
    }

    return t
}

export const apply3 = (m: Mat3, x: number, y: number, z: number): [number, number, number] => [
    m[0] * x + m[1] * y + m[2] * z,
    m[3] * x + m[4] * y + m[5] * z,
    m[6] * x + m[7] * y + m[8] * z
]

/** Unit vector for right ascension (degrees) and declination (degrees). */
export const raDecToVector = (raDeg: number, decDeg: number): [number, number, number] => {
    const ra = raDeg * DEG
    const dec = decDeg * DEG
    const c = Math.cos(dec)

    return [c * Math.cos(ra), c * Math.sin(ra), Math.sin(dec)]
}

/** Right ascension in hours and declination in degrees for a J2000 unit vector. */
export const vectorToRaDec = (x: number, y: number, z: number): { ra: number, dec: number } => {
    const length = Math.hypot(x, y, z) || 1
    let ra = Math.atan2(y, x) * RAD / 15

    if (ra < 0) ra += 24

    return { ra, dec: Math.asin(Math.max(-1, Math.min(1, z / length))) * RAD }
}

/** Altitude and azimuth (degrees, azimuth clockwise from north) from a horizon-frame vector. */
export const horizonToAltAz = (x: number, y: number, z: number): { alt: number, az: number } => {
    let az = Math.atan2(-y, x) * RAD

    if (az < 0) az += 360

    return { alt: Math.asin(Math.max(-1, Math.min(1, z))) * RAD, az }
}

export const localSiderealHours = (date: Date): number => {
    const gast = SiderealTime(date)

    return (((gast + OBSERVATORY.longitude / 15) % 24) + 24) % 24
}

const COMPASS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']

export const compassPoint = (azimuth: number): string => COMPASS[Math.round((((azimuth % 360) + 360) % 360) / 22.5) % 16]

export const COMPASS_WORDS: Record<string, string> = {
    N: 'north',
    NNE: 'north-north-east',
    NE: 'north-east',
    ENE: 'east-north-east',
    E: 'east',
    ESE: 'east-south-east',
    SE: 'south-east',
    SSE: 'south-south-east',
    S: 'south',
    SSW: 'south-south-west',
    SW: 'south-west',
    WSW: 'west-south-west',
    W: 'west',
    WNW: 'west-north-west',
    NW: 'north-west',
    NNW: 'north-north-west'
}

/** Approximate star colour from the B-V index, softened toward white the way the eye sees it. */
export const bvToRgb = (bv: number, saturation = 0.55): [number, number, number] => {
    const index = Math.max(-0.4, Math.min(2, bv))
    const temperature = 4600 * (1 / (0.92 * index + 1.7) + 1 / (0.92 * index + 0.62))
    const t = temperature / 100
    let r: number
    let g: number
    let b: number

    if (t <= 66) {
        r = 255
        g = 99.4708025861 * Math.log(t) - 161.1195681661
        b = t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307
    }
    else {
        r = 329.698727446 * Math.pow(t - 60, -0.1332047592)
        g = 288.1221695283 * Math.pow(t - 60, -0.0755148492)
        b = 255
    }

    const clamp = (value: number) => Math.max(0, Math.min(255, value)) / 255
    const mix = (value: number, white: number) => white + (value - white) * saturation

    return [mix(clamp(r), 1), mix(clamp(g), 0.97), mix(clamp(b), 0.93)]
}
